import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, Button,
  Chip, Stack, Avatar, Paper, Fade, CircularProgress
} from '@mui/material';
import {
  AccountBalance, Groups, LocationCity, Flag,
  Campaign, AutoAwesome, CheckCircle, PlayCircleOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../contexts/TemplateContext'; // <-- Apna Context import karein

// --- Premium Colors & Theme Config ---
const themeColors = {
  primaryBg: '#FFF8E1',
  triSaffron: '#FF9933',
  triWhite: '#FFFFFF',
  triGreen: '#138808',
  saffronMain: '#FF6D00',
  greenMain: '#00C853',
  textDark: '#263238',
  cardBg: '#ffffff',
};


const defaultIcons = [<AccountBalance />, <Groups />, <LocationCity />, <Flag />, <Campaign />];
const defaultColors = [themeColors.saffronMain, '#0277bd', themeColors.greenMain, '#c62828', '#8e24aa'];

export default function HomePage() {
  const navigate = useNavigate();
  const { fetchGroups, fetchTemplates } = useTemplate(); // Context se API functions nikale

  // --- States ---
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);


  useEffect(() => {
    const loadGroups = async () => {
      setLoadingGroups(true);
      const res = await fetchGroups();

      if (res.success && res.data.length > 0) {
        setGroups(res.data);
        setSelectedGroupId(res.data[0].id);
      }
      setLoadingGroups(false);
    };

    loadGroups();
  }, [fetchGroups]);


  useEffect(() => {
    if (!selectedGroupId) return;

    const loadTemplates = async () => {
      setLoadingTemplates(true);

      const res = await fetchTemplates({ groupId: selectedGroupId, subGroupId: 'all' });

      if (res.success) {
        setTemplates(res.data);
      } else {
        setTemplates([]);
      }
      setLoadingTemplates(false);
    };

    loadTemplates();
  }, [selectedGroupId, fetchTemplates]);


  return (
    <Box sx={{ bgcolor: themeColors.primaryBg, minHeight: '100vh', pb: 12 }}>

      {/* 1. Hero Section (Tiranga Theme) */}
      <Paper
        elevation={4}
        sx={{
          background: `linear-gradient(180deg, ${themeColors.triSaffron} 0%, ${themeColors.triWhite} 50%, ${themeColors.triGreen} 100%)`,
          color: 'white',
          pt: { xs: 4, md: 6 },
          pb: { xs: 6, md: 8 },
          borderRadius: { xs: '0 0 24px 24px', md: '0 0 40px 40px' },
          position: 'relative',
          overflow: 'hidden',
          mt: '-1px'
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1 }} />
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px', zIndex: 1
        }} />

        <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3.5rem' }, textShadow: '0px 2px 4px rgba(0,0,0,0.5)' }}>
            भारत का No.1 राजनैतिक <br /> पोस्टर प्लेटफार्म
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, fontWeight: 500, fontSize: { xs: '1rem', md: '1.3rem' }, px: 2, textShadow: '0px 1px 2px rgba(0,0,0,0.5)' }}>
            ग्राम पंचायत से लेकर सांसद तक - 10 सेकंड में बनाएं अपना शानदार पोस्टर!
          </Typography>

          <Stack direction="row" spacing={{ xs: 2, md: 6 }} justifyContent="center"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
              py: 2, px: { xs: 2, md: 4 }, mx: { xs: 2, md: 0 },
              borderRadius: 4, display: 'inline-flex',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.3)'
            }}>
            {[
              { num: '10K+', label: 'नेता जुड़े हैं' },
              { num: '50K+', label: 'पोस्टर बने' },
              { num: '500+', label: 'डिज़ाइन' }
            ].map((stat, index) => (
              <Box key={index} sx={{ textAlign: 'center', minWidth: { xs: '70px', md: 'auto' } }}>
                <Typography variant="h5" fontWeight="900" sx={{ color: '#fff', fontSize: { xs: '1.25rem', md: '1.8rem' } }}>{stat.num}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.75rem', md: '0.9rem' } }}>{stat.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 5 }}>

        {/* Step 1: Dynamic Category (Groups) Selection */}
        <Typography variant="h6" fontWeight="800" color={themeColors.textDark} gutterBottom sx={{ textAlign: 'center', textTransform: 'uppercase', mb: 2, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          1. अपना स्तर चुनें (Choose Level)
        </Typography>

        {loadingGroups ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="warning" />
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: { xs: 1.5, md: 2 },
            mb: 5
          }}>
            {groups.map((group, index) => {
              const isSelected = selectedGroupId === group.id;

              // API me agar image na ho to default color/icon assign kar rahe hain
              const catColor = defaultColors[index % defaultColors.length];
              const catIcon = defaultIcons[index % defaultIcons.length];

              return (
                <Paper
                  key={group.id}
                  elevation={isSelected ? 6 : 1}
                  onClick={() => setSelectedGroupId(group.id)}
                  sx={{
                    cursor: 'pointer', textAlign: 'center', p: { xs: 2, md: 3 },
                    borderRadius: 4, position: 'relative',
                    bgcolor: themeColors.cardBg,
                    border: isSelected ? `2px solid ${catColor}` : '2px solid transparent',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: `0 8px 20px ${catColor}30` }
                  }}
                >
                  {isSelected && (
                    <Fade in={true}>
                      <CheckCircle sx={{ position: 'absolute', top: 8, right: 8, color: catColor, fontSize: 20 }} />
                    </Fade>
                  )}
                  <Avatar sx={{
                    bgcolor: isSelected ? catColor : `${catColor}15`,
                    color: isSelected ? 'white' : catColor,
                    width: { xs: 48, md: 64 }, height: { xs: 48, md: 64 }, mx: 'auto', mb: 1.5,
                  }}>
                    {group.image_url ? (
                      <img src={group.image_url} alt={group.group_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      catIcon
                    )}
                  </Avatar>
                  <Typography variant="subtitle2" fontWeight={isSelected ? "800" : "600"} color={isSelected ? themeColors.textDark : '#64748B'} sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                    {group.group_name}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Step 2: Dynamic Templates Display */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="800" mb={2} color={themeColors.textDark} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
            <AutoAwesome sx={{ mr: 1, color: themeColors.saffronMain, fontSize: 20 }} />
            उपलब्ध डिज़ाइन ({templates.length})
          </Typography>

          {loadingTemplates ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="success" />
            </Box>
          ) : templates.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2.5, sm: 2, md: 3 } 
            }}>
              {templates.map((template) => {
                const isVideo = template.sample_url && template.sample_url.includes('.mp4');

                return (
                  <Card key={template.id} sx={{
                    borderRadius: 4, 
                    overflow: 'hidden',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.08)', 
                    border: '1px solid #F1F5F9',
                    display: 'flex', flexDirection: 'column',
                    maxWidth: { xs: '100%', sm: '100%' },
                    mx: 'auto',
                    width: '100%'
                  }}>

                    {/* Media Preview (Video/Image) */}
                    <Box sx={{
                      position: 'relative',
                      bgcolor: '#e2e8f0',
                      height: { xs: 280, sm: 220, md: 220 },
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {isVideo ? (
                        <video
                          src={template.sample_url}
                          poster={template.sample_thumbnail}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          controls
                        />
                      ) : (
                        <img
                          src={template.sample_url || template.sample_thumbnail}
                          alt="Template"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      )}

                      {/* Premium/Free Badge */}
                      <Chip
                        label={template.sale_price > 0 ? 'PREMIUM' : 'FREE'}
                        sx={{
                          position: 'absolute', top: 12, right: 12,
                          bgcolor: template.sale_price > 0 ? themeColors.saffronMain : themeColors.greenMain,
                          color: 'white', fontWeight: '900',
                          fontSize: { xs: '0.75rem', md: '0.65rem' }, // मोबाइल पर बैज भी थोड़ा बड़ा
                          height: 24, zIndex: 2
                        }}
                      />
                    </Box>

                    {/* Card Content */}
                    <Box sx={{ p: { xs: 2.5, md: 2 }, textAlign: 'center', bgcolor: 'white', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="800"
                        sx={{
                          mb: 2,
                          color: themeColors.textDark,
                          fontSize: { xs: '1.1rem', md: '0.95rem' }, // नाम का फॉन्ट बड़ा कर दिया
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.3
                        }}
                      >
                        {template.name || 'शानदार डिज़ाइन'}
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large" // बटन बड़ा कर दिया
                        startIcon={<AutoAwesome sx={{ fontSize: { xs: 20, md: 20 } }} />}
                        onClick={() => navigate(`/template-details/${template.id}`)}
                        sx={{
                          borderRadius: 2, py: { xs: 1.2, md: 1 }, // मोबाइल पर बटन की पैडिंग बढ़ा दी
                          fontSize: { xs: '1rem', md: '0.9rem' }, textTransform: 'none', fontWeight: 'bold',
                          bgcolor: themeColors.saffronMain, boxShadow: 'none',
                          '&:hover': { bgcolor: '#E65100', boxShadow: '0 4px 10px rgba(230, 81, 0, 0.3)' }
                        }}
                      >
                        बनाएं
                      </Button>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box textAlign="center" py={6} sx={{ bgcolor: 'white', borderRadius: 4, border: '1px dashed #CBD5E1' }}>
              <AutoAwesome sx={{ fontSize: 60, color: '#E2E8F0', mb: 1 }} />
              <Typography variant="subtitle1" color="#64748B" fontWeight="700">इस ग्रुप में कोई डिज़ाइन नहीं मिला।</Typography>
            </Box>
          )}
        </Box>

      </Container>
    </Box>
  );
}